import { useAuthStore } from '@client/stores/AuthStore';
import { debug } from '@client/utils/logger';

type SessionExpiredListener = () => void;

const listeners = new Set<SessionExpiredListener>();

/**
 * Registers a callback invoked once per session-expiry transition.
 *
 * Uses a Set so registering the same callback twice is a no-op — safe for
 * module-level subscriptions that may be re-evaluated during HMR or test setup.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Idempotently transitions the client into the expired-session state.
 *
 * Clears the auth token, marks the session expired, and notifies subscribers
 * (e.g. the tRPC query client) so protected pages stop issuing work during the
 * redirect window. Subsequent calls while already expired are no-ops.
 */
export function expireSession(reason: string) {
    const authStore = useAuthStore.getState();

    if (authStore.authTokenExpired) {
        // Load-bearing idempotency guard: listeners such as queryClient.cancelQueries()
        // must only run on the first transition into the expired-session state.
        return;
    }

    debug(reason);
    authStore.clearAuthToken();
    authStore.setAuthTokenExpired(true);

    for (const listener of listeners) {
        try {
            listener();
        } catch (error) {
            console.error('[sessionExpiry] listener threw:', error);
        }
    }
}
